import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getUsers, createUser, deleteUser, getPendingInvites, resendInvite } from '../../services/api';

const ManageUsers = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    isAdmin: false
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, pendingRes] = await Promise.all([
        getUsers(),
        getPendingInvites()
      ]);
      setUsers(usersRes.data);
      setPendingInvites(pendingRes.data);
    } catch (err) {
      setError('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    setInviteLink('');

    try {
      const response = await createUser(formData);
      const fullLink = `${window.location.origin}${response.data.inviteLink}`;
      setInviteLink(fullLink);
      setFormData({ username: '', isAdmin: false });
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nella creazione');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendInvite = async (userId) => {
    try {
      const response = await resendInvite(userId);
      const fullLink = `${window.location.origin}${response.data.inviteLink}`;
      setInviteLink(fullLink);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel reinvio');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo utente?')) return;

    try {
      await deleteUser(userId);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nell\'eliminazione');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const isExpired = (dateString) => {
    return new Date(dateString) < new Date();
  };

  if (loading) return <div className="loading">Caricamento...</div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <Link to="/admin" className="back-link">← Torna al pannello admin</Link>
      </div>

      <div className="page-header-flex">
        <h1>Gestione Utenti</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn btn-primary">
          {showForm ? 'Annulla' : 'Nuovo utente'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <form onSubmit={handleSubmit} className="admin-form">
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.isAdmin}
                onChange={(e) => setFormData({ ...formData, isAdmin: e.target.checked })}
              />
              <span>Amministratore</span>
            </label>
          </div>

          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Creazione...' : 'Crea utente'}
          </button>
        </form>
      )}

      {inviteLink && (
        <div className="invite-link-box">
          <h3>Link di invito generato</h3>
          <p>Copia questo link e invialo all'utente:</p>
          <div className="invite-link-row">
            <input
              type="text"
              value={inviteLink}
              readOnly
              className="invite-link-input"
            />
            <button onClick={copyToClipboard} className="btn btn-secondary">
              {copied ? 'Copiato!' : 'Copia'}
            </button>
          </div>
          <p className="text-muted">Il link scade tra 7 giorni</p>
          <button
            onClick={() => setInviteLink('')}
            className="btn btn-text"
          >
            Chiudi
          </button>
        </div>
      )}

      {pendingInvites.length > 0 && (
        <div className="pending-invites-section">
          <h2>Inviti in attesa ({pendingInvites.length})</h2>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Scadenza</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {pendingInvites.map((invite) => (
                  <tr key={invite._id} className={isExpired(invite.inviteExpires) ? 'expired-row' : ''}>
                    <td data-label="Username">
                      {invite.username}
                      {invite.isAdmin && <span className="admin-badge">Admin</span>}
                    </td>
                    <td data-label="Scadenza">
                      {formatDate(invite.inviteExpires)}
                      {isExpired(invite.inviteExpires) && <span className="expired-badge">Scaduto</span>}
                    </td>
                    <td className="actions-cell">
                      <button
                        onClick={() => handleResendInvite(invite._id)}
                        className="btn btn-secondary btn-small"
                      >
                        Reinvia
                      </button>
                      <button
                        onClick={() => handleDelete(invite._id)}
                        className="btn btn-danger btn-small"
                      >
                        Elimina
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <h2>Utenti attivi</h2>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Ruolo</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            <tr className="current-user-row">
              <td data-label="Username">{currentUser.username}</td>
              <td data-label="Ruolo">{currentUser.isAdmin ? 'Admin' : 'Utente'}</td>
              <td className="actions-cell"><span className="text-muted">(tu)</span></td>
            </tr>
            {users.filter(u => u.isActivated !== false).map((user) => (
              <tr key={user._id}>
                <td data-label="Username">{user.username}</td>
                <td data-label="Ruolo">{user.isAdmin ? 'Admin' : 'Utente'}</td>
                <td className="actions-cell">
                  <button
                    onClick={() => handleDelete(user._id)}
                    className="btn btn-danger btn-small"
                  >
                    Elimina
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManageUsers;
